import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { FormInput, FormButton, DaySelector } from "./FormComponents";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { updateHabitById, getHabitById } from "../store/slices/habitsSlice";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { DayOfWeek } from "../types";
import AppSidebar from "./AppSidebar";
import { useTheme } from "../hooks/useTheme";

const EditHabit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const { isDark, setTheme } = useTheme();
  const {
    loading,
    error,
    items: habits,
  } = useAppSelector((state) => state.habits);
  const userProfile = useAppSelector((state) => state.user.profile);
  const initials = userProfile?.name
    ? userProfile.name
        .split(" ")
        .map((w: string) => w.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("")
    : (userProfile?.username?.charAt(0).toUpperCase() ?? "U");

  const handleThemeToggle = () => setTheme(isDark ? "light" : "dark");

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  // Find the habit from state or fetch it
  const habit = habits.find((h) => h.id === id);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    activeForDays: [] as DayOfWeek[],
    isPaused: false,
  });

  // Load habit data
  useEffect(() => {
    trackPageVisit("edit_habit");
    if (habit) {
      setFormData({
        name: habit.name,
        activeForDays: habit.activeForDays || [],
        isPaused: habit.isPaused || false,
      });
    } else if (id) {
      // Fetch habit if not in state
      dispatch(getHabitById(id));
    }
  }, [habit, id, dispatch]);

  // Update form when habit is loaded
  useEffect(() => {
    if (habit) {
      setFormData({
        name: habit.name,
        activeForDays: habit.activeForDays || [],
        isPaused: habit.isPaused || false,
      });
    }
  }, [habit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !id) {
      return;
    }

    try {
      // Dispatch updateHabitById action
      await dispatch(
        updateHabitById({
          id,
          updates: {
            name: formData.name.trim(),
            activeForDays:
              formData.activeForDays.length > 0
                ? formData.activeForDays
                : undefined,
            isPaused: formData.isPaused,
          },
        }),
      ).unwrap();

      // Track habit editing event
      trackUserInteraction("edit_habit", "habit_management", id);

      // Navigate to habits page
      navigate("/habits");
    } catch (error) {
      console.error("Error updating habit:", error);
      // Error is handled by Redux state
    }
  };

  const handleCancel = () => {
    navigate("/habits");
  };

  const handleDayToggle = (day: DayOfWeek) => {
    setFormData((prev) => ({
      ...prev,
      activeForDays: prev.activeForDays.includes(day)
        ? prev.activeForDays.filter((d) => d !== day)
        : [...prev.activeForDays, day],
    }));
  };

  if (!habit && !loading) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#f5a623]/20 border border-[#f5a623]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🔥</span>
          </div>
          <h2 className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white mb-2">
            {t("editHabit.notFoundTitle") as string}
          </h2>
          <p className="text-[17px] text-gray-500 dark:text-white/40 mb-6">
            {t("editHabit.notFoundMessage") as string}
          </p>
          <button
            onClick={() => navigate("/habits")}
            className="px-5 py-2.5 bg-[#f5a623] text-[#1a1300] text-[18px] font-semibold rounded-xl hover:opacity-90 transition-all"
          >
            {t("editHabit.goBackToCalendar") as string}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title={t("editHabit.title")}
        description={t("editHabit.description") as string}
        keywords={t("editHabit.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#f5a623]/20 border border-[#f5a623]/30 flex items-center justify-center transition-all hover:bg-[#f5a623]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#f5a623"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("editHabit.headerTitle")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#f5a623]/15 border border-[#f5a623]/25 flex items-center justify-center transition-all hover:bg-[#f5a623]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#f5a623"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("editHabit.headerTitle")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="space-y-4">
              <div className="rounded-xl border border-[#f5a623]/20 dark:border-[#f5a623]/30 bg-gradient-to-br from-[#f5a623]/[0.06] to-transparent dark:from-[#f5a623]/[0.08] p-4 lg:p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#f5a623]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[24px] leading-none">🔥</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-[19px] font-semibold text-gray-900 dark:text-white mb-1">
                      {t("editHabit.aboutDailyHabitsTitle") as string}
                    </p>
                    <p className="text-[16px] text-gray-600 dark:text-white/45 leading-relaxed">
                      {t("editHabit.dailyHabitsText") as string}
                    </p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-[#f5a623]/25 bg-[#f5a623]/10 px-3 py-2">
                    <p className="text-[14px] uppercase tracking-wider text-[#7a5a12] dark:text-[#f5cf7b]/80">
                      {t("editHabit.activeDaysLabel") as string}
                    </p>
                    <p className="text-[18px] font-semibold tracking-tight text-[#5c430e] dark:text-[#f9de9f]">
                      {formData.activeForDays.length}
                    </p>
                  </div>
                  <div className="rounded-lg border border-black/[0.08] dark:border-white/[0.12] bg-white/[0.6] dark:bg-white/[0.04] px-3 py-2">
                    <p className="text-[14px] uppercase tracking-wider text-gray-500 dark:text-white/35">
                      {t("editHabit.headerTitle") as string}
                    </p>
                    <p className="text-[18px] font-semibold tracking-tight text-gray-900 dark:text-white">
                      {formData.isPaused
                        ? t("editHabit.habitPaused")
                        : t("editHabit.habitActive")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl">
                {error && (
                  <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                    <div className="flex items-center">
                      <svg
                        className="w-5 h-5 text-red-500 mr-3 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-red-700 dark:text-red-300 text-sm font-medium">
                        {error}
                      </p>
                    </div>
                  </div>
                )}
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4 lg:space-y-5"
                >
                  <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4 lg:p-5">
                    <FormInput
                      id="name"
                      label={t("editHabit.habitNameLabel") as string}
                      value={formData.name}
                      onChange={(value) =>
                        setFormData({ ...formData, name: value })
                      }
                      placeholder={
                        t("editHabit.habitNamePlaceholder") as string
                      }
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4 lg:p-5">
                      <DaySelector
                        selectedDays={formData.activeForDays}
                        onDayToggle={handleDayToggle}
                        label={t("editHabit.activeDaysLabel") as string}
                      />
                    </div>

                    {/* Habit Status */}
                    <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4 lg:p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-[#3b5cde]/20 rounded-lg flex items-center justify-center shrink-0">
                          <span className="text-lg">
                            {formData.isPaused ? "⏸️" : "▶️"}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-[17px] font-semibold text-gray-900 dark:text-white mb-1">
                            {formData.isPaused
                              ? t("editHabit.habitPaused")
                              : t("editHabit.habitActive")}
                          </p>
                          <p className="text-[16px] text-gray-500 dark:text-white/40 leading-relaxed mb-3">
                            {formData.isPaused
                              ? t("editHabit.resumeDescription")
                              : t("editHabit.pauseDescription")}
                          </p>
                          <FormButton
                            type="button"
                            variant={
                              formData.isPaused ? "primary" : "secondary"
                            }
                            onClick={() =>
                              setFormData({
                                ...formData,
                                isPaused: !formData.isPaused,
                              })
                            }
                            size="sm"
                          >
                            {formData.isPaused
                              ? t("editHabit.resumeHabit")
                              : t("editHabit.pauseHabit")}
                          </FormButton>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#f5a623]/20 rounded-lg flex items-center justify-center shrink-0">
                        <span className="text-lg">ℹ️</span>
                      </div>
                      <div>
                        <p className="text-[17px] font-semibold text-gray-900 dark:text-white mb-1">
                          {t("editHabit.aboutDailyHabitsTitle") as string}
                        </p>
                        <p className="text-[16px] text-gray-500 dark:text-white/40 leading-relaxed">
                          {t("editHabit.dailyHabitsText") as string}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-black/[0.07] dark:border-white/[0.08]">
                    <div className="">
                      <FormButton
                        type="submit"
                        variant="primary"
                        loading={loading}
                        disabled={!formData.name.trim() || loading}
                        size="lg"
                      >
                        {loading
                          ? (t("editHabit.updating") as string)
                          : (t("editHabit.updateHabit") as string)}
                      </FormButton>
                    </div>
                    <div className="">
                      <FormButton
                        type="button"
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={loading}
                        size="lg"
                      >
                        {t("editHabit.cancel") as string}
                      </FormButton>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* MOBILE BOTTOM NAV */}
          <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 border-t border-black/[0.07] dark:border-white/[0.08] backdrop-blur-sm">
            <div className="flex justify-around px-2 pt-2 pb-5">
              {mobileNavLinks.map(({ path, emoji, label }) => {
                const active = isNavActive(path);
                return (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className="flex flex-col items-center gap-1"
                  >
                    <span className="text-[22px]">{emoji}</span>
                    <span
                      className={`text-[13px] font-medium ${active ? "text-[#f5a623]" : "text-gray-400 dark:text-white/25"}`}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>
        </main>
      </div>
    </>
  );
};

export default EditHabit;
